import { create } from 'zustand';
import { devtools } from 'zustand/middleware';

export interface WineCase {
  id: string;
  name: string;
  quantity: number;
  image: string;
}

interface FlexState {
  cart: WineCase[];
  caseDetails: { casePrice: number; savePrice: number; totalQuantity: number };
  isDirty: boolean;
  isApiLoading: boolean;
  mobileFilterOpen: boolean;
  searchResults: any[];
  showFloatingCart: boolean;
  accordionValue: string[];
  modalOpen: boolean;
}

interface FlexActions {
  addWineCase: (wineCase: WineCase) => void;
  updateWineCaseQuantity: (id: string, quantity: number) => void;
  removeWineCase: (id: string) => void;
  updateWineCase: (id: string, updates: Partial<WineCase>) => void;
  clearCart: () => void;
  setCaseDetails: (details: FlexState['caseDetails']) => void;
  setIsApiLoading: (loading: boolean) => void;
  setMobileFilterOpen: (open: boolean) => void;
  setSearchResults: (results: any[]) => void;
  setShowFloatingCart: (show: boolean) => void;
  setAccordionValue: (value: string[]) => void;
  setModalOpen: (open: boolean) => void;
  reset: () => void;
}

export type FlexStore = FlexState & FlexActions;

const initialState: FlexState = {
  cart: [],
  caseDetails: { casePrice: 0, savePrice: 0, totalQuantity: 0 },
  isDirty: false,
  isApiLoading: false,
  mobileFilterOpen: false,
  searchResults: [],
  showFloatingCart: false,
  accordionValue: [],
  modalOpen: false,
};

export const useFlexStore = create<FlexStore>()(
  devtools(
    (set, get) => ({
      ...initialState,
      addWineCase: (wineCase) => {
        const currentCart = get().cart;
        const existingCase = currentCart.find(item => item.id === wineCase.id);
        if (existingCase) {
          const updatedCart = currentCart.map(item =>
            item.id === wineCase.id ? { ...item, quantity: item.quantity + (wineCase.quantity || 1) } : item,
          );
          set({ cart: updatedCart, isDirty: true }, undefined, 'addWineCase');
        } else {
          set({ cart: [...currentCart, { ...wineCase, quantity: wineCase.quantity || 1 }], isDirty: true }, undefined, 'addWineCase');
        }
      },
      updateWineCaseQuantity: (id, quantity) => {
        const currentCart = get().cart;
        if (quantity <= 0) {
          set({ cart: currentCart.filter(item => item.id !== id), isDirty: true }, undefined, 'updateWineCaseQuantity');
        } else {
          set({ cart: currentCart.map(item => item.id === id ? { ...item, quantity } : item), isDirty: true }, undefined, 'updateWineCaseQuantity');
        }
      },
      removeWineCase: (id) => {
        set({ cart: get().cart.filter(item => item.id !== id), isDirty: true }, undefined, 'removeWineCase');
      },
      updateWineCase: (id, updates) => {
        set({ cart: get().cart.map(item => item.id === id ? { ...item, ...updates } : item), isDirty: true }, undefined, 'updateWineCase');
      },
      clearCart: () => set({ cart: [], isDirty: true }, undefined, 'clearCart'),
      setCaseDetails: (details) => set({ caseDetails: details }, undefined, 'setCaseDetails'),
      setIsApiLoading: (loading) => set({ isApiLoading: loading }, undefined, 'setIsApiLoading'),
      setMobileFilterOpen: (open) => set({ mobileFilterOpen: open }, undefined, 'setMobileFilterOpen'),
      setSearchResults: (results) => set({ searchResults: results }, undefined, 'setSearchResults'),
      setShowFloatingCart: (show) => set({ showFloatingCart: show }, undefined, 'setShowFloatingCart'),
      setAccordionValue: (value) => set({ accordionValue: value }, undefined, 'setAccordionValue'),
      setModalOpen: (open) => set({ modalOpen: open }, undefined, 'setModalOpen'),
      reset: () => set(initialState, undefined, 'reset'),
    }),
    { name: 'FlexStore' },
  ),
);

// Selectors
export const selectFlexCartTotalItems = (state: FlexState) => state.caseDetails.totalQuantity;
export const selectFlexCartTotalPrice = (state: FlexState) => state.caseDetails.casePrice;
export const selectFlexCartTotalDiscount = (state: FlexState) => state.caseDetails.savePrice;
export const selectIsFlexCartEmpty = (state: FlexState) => state.cart.length === 0;
export const selectFlexSearchResults = (state: FlexState) => state.searchResults;

/** Parameterized selector — call with product ID */
export const selectFlexCartCountByProduct = (state: FlexState, productId: string) => {
  const item = state.cart.find(wineCase => wineCase.id === productId);
  return item ? item.quantity : 0;
};
